import React from 'react';
import styled from 'styled-components';
import { H3, Message, ModalFactory, ModalHeader, ModalLayout, ModalTitle, Text } from 'components';
import { theme, toRem } from 'design';
import { IEmployerSite } from 'types';
import { formatJobTitle, getEmployeeStatusDisplayProps, toTitleCase } from 'utils';

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${toRem(theme.space.md)};
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: ${toRem(11)};
  background-color: ${theme.color.neutral[50]};
  padding: ${toRem(theme.space.md)};
  gap: ${toRem(theme.space.md)};
`;

const DetailRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: ${toRem(theme.space.md)};
  width: 100%;
`;

const Label = styled.label`
  font-size: ${toRem(theme.font.size.xxs)};
  font-weight: ${theme.font.weight.medium};
  color: ${theme.color.neutral[500]};
`;

const StyledMessage = styled(Message)`
  padding: ${toRem(theme.space.md)} ${toRem(theme.space.xl)};
`;

interface IEmployerDetailsProps {
  employer: IEmployerSite;
  onClose: () => void;
}

const EmployerDetails = ({ employer, onClose }: IEmployerDetailsProps) => {
  if (!employer) return null;

  const status = getEmployeeStatusDisplayProps(employer.status, employer.actionRequired);

  return (
    <ModalFactory customModal onClose={onClose} isActive>
      <ModalLayout maxWidth={500} onClose={onClose} isActive data-cy="employer-details-modal">
        <ModalHeader>
          <ModalTitle>Employer Details</ModalTitle>
        </ModalHeader>
        <Content>
          <Details>
            {status && <StyledMessage appearance={status.appearance} message={status.text} />}
            <H3 allowWordWrap>{employer?.name}</H3>
            {employer?.employee?.name && (
              <DetailRow data-cy="preferred-name">
                <Label>Preferred Name</Label>
                <Text size="xsmall">{employer.employee.name}</Text>
              </DetailRow>
            )}
            <DetailRow data-cy="role">
              <Label>Role</Label>
              <Text size="xsmall">{toTitleCase(employer?.role)}</Text>
            </DetailRow>
            <DetailRow data-cy="job-title">
              <Label>Job Title</Label>
              <Text size="xsmall">{employer?.job ? formatJobTitle(employer?.job?.title, employer?.job?.classification) : '--'}</Text>
            </DetailRow>
            <DetailRow data-cy="employer-id">
              <Label>Employee ID</Label>
              <Text size="xsmall">{employer.employeeId || '--'}</Text>
            </DetailRow>
            <DetailRow data-cy="disbursement">
              <Label>Disbursement</Label>
              <Text size="xsmall">{toTitleCase(employer.disbursementMethod)}</Text>
            </DetailRow>
          </Details>
        </Content>
      </ModalLayout>
    </ModalFactory>
  );
};
export default EmployerDetails;
